import { auth } from '@/src/infrastructure/config/firebase'
import { logout } from '@/src/modules/auth/session'
import type {
  ApiDraft,
  ApiInvitation,
  ApiInvitationStatusResponse,
  ApiOccasion,
  ApiTemplate,
  BackendOccasionType,
  InvitationHistoryEntry,
  InvitationListQuery,
  InvitationStats,
  PaginatedResponse,
  WizardData,
} from '@/src/types'
import { trackEvent } from '@/src/infrastructure/lib/analytics'
import { wizardSession } from '@/src/infrastructure/lib/behaviour-analytics'
import { templateContextParams } from '@/src/infrastructure/lib/template-analytics'

export const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:4400') + '/v1'

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// In production, VITE_API_URL must be set at build time (baked into the JS
// bundle by Vite). If it is missing the app silently points at localhost,
// which works only for the developer running a local backend — all other
// visitors will see API failures. Warn loudly in the console so the problem
// is obvious during staging validation.
if (import.meta.env.PROD && !import.meta.env.VITE_API_URL) {
  console.error(
    '[InviteCloud] VITE_API_URL is not set. The production build is calling ' +
    'http://localhost:4000 — this only works for the developer running a local ' +
    'backend. Set VITE_API_URL to the production API URL in your Cloudflare ' +
    'Pages / CI environment variables and redeploy.',
  )
}

export async function request<T>(
  url: string,
  options: RequestInit = {},
  retried = false,
): Promise<T> {
  const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
  // Firebase auto-refreshes ID tokens; on a retry we force a fresh one in case
  // the token expired mid-flight.
  const token = isBypass
    ? 'dev-token'
    : await auth.currentUser?.getIdToken(retried);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }

  const res = await fetch(url, { ...options, headers })

  if (res.status === 401 && !retried && !isBypass && auth.currentUser) {
    // Retry once with a force-refreshed token before giving up.
    return request<T>(url, options, true)
  }
  if (res.status === 401) {
    if (isBypass) {
      throw new Error('Unauthorized: ensure backend is also running with DEV_AUTH_BYPASS=true');
    }
    await logout().catch(() => undefined)
    window.location.href = '/login'
    throw new Error('Session expired')
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const message = Array.isArray(body?.message)
      ? body.message.join(', ')
      : body?.message ?? `HTTP ${res.status}`
    throw new ApiError(message, res.status, body)
  }

  if (res.status === 204) return undefined as T

  const body = await res.json()
  return (body?.data !== undefined ? body.data : body) as T
}

/**
 * Legacy listing — kept for the old Dashboard page until it migrates to
 * `listInvitations`. The backend now returns a paginated envelope, so we
 * unwrap `items` here and bump the limit so we still get every row.
 */
export async function getInvitations() {
  const res = await request<PaginatedResponse<ApiInvitation>>(
    `${API_BASE}/invitations?limit=100`,
  )
  return res.items
}

/**
 * Paginated, filterable, sortable listing for the Invitations Dashboard.
 * Mirrors the ListInvitationsDto on the backend.
 */
export function listInvitations(query: InvitationListQuery = {}) {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value))
    }
  }
  const qs = params.toString()
  return request<PaginatedResponse<ApiInvitation>>(
    `${API_BASE}/invitations${qs ? `?${qs}` : ''}`,
  )
}

export function getInvitationStats() {
  return request<InvitationStats>(`${API_BASE}/invitations/stats`)
}

export function getInvitationHistory(id: string) {
  return request<InvitationHistoryEntry[]>(`${API_BASE}/invitations/${id}/history`)
}

export function archiveInvitation(id: string) {
  return request<ApiInvitation>(`${API_BASE}/invitations/${id}/archive`, {
    method: 'POST',
  })
}

export function restoreInvitation(id: string) {
  return request<ApiInvitation>(`${API_BASE}/invitations/${id}/restore`, {
    method: 'POST',
  })
}

export function cancelInvitation(id: string, reason?: string) {
  return request<ApiInvitation>(`${API_BASE}/invitations/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

export function extendInvitation(id: string, extensionDays: number = 7) {
  return request<ApiInvitation>(`${API_BASE}/invitations/${id}/extend`, {
    method: 'POST',
    body: JSON.stringify({ extensionDays }),
  })
}

export function getInvitationStatus(id: string) {
  return request<ApiInvitationStatusResponse>(`${API_BASE}/invitations/${id}/status`)
}

export function createInvitation(payload: WizardData & { mediaUrls: string[] }) {
  return request<ApiInvitation>(`${API_BASE}/invitations`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function updateInvitation(id: string, payload: Partial<WizardData>) {
  return request<ApiInvitation>(`${API_BASE}/invitations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
}

// ── Draft autosave + recovery ───────────────────────────────────────────────
// Drafts are Invitation rows in DRAFT status. The wizard creates one as soon as
// a template is chosen, then PUTs partial updates on a debounce. `draftMeta`
// carries the full wizard-restore state so a refresh/logout/device-switch can
// rehydrate exactly where the user left off (server-side, keyed by user).

export type DraftPayload = Partial<WizardData> & {
  draftMeta?: Record<string, unknown>
}

/** Create a server-side DRAFT. Only templateId + occasionType are required. */
export function createDraft(
  payload: DraftPayload & { templateId: string; occasionType: BackendOccasionType },
) {
  return request<ApiDraft>(`${API_BASE}/invitations/draft`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }).then((draft) => {
    trackEvent('INVITATION_CREATED', 'invitation', payload.occasionType, undefined, { occasion_type: payload.occasionType, template_id: payload.templateId });
    return draft;
  })
}

/** Autosave a DRAFT (partial). Reuses the existing invitation PUT endpoint. */
export function saveDraft(id: string, payload: DraftPayload, token?: string | null) {
  const url = token
    ? `${API_BASE}/invitations/${id}?token=${encodeURIComponent(token)}`
    : `${API_BASE}/invitations/${id}`;
  return request<ApiDraft>(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

/** Fetch a single draft for wizard rehydration. Supports tokenized access for shared/MCP drafts. */
export async function getDraft(id: string, token?: string | null): Promise<ApiDraft> {
  const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
  const isLoggedIn = isBypass || Boolean(auth.currentUser);

  if (isLoggedIn) {
    try {
      const url = token
        ? `${API_BASE}/invitations/${id}?token=${encodeURIComponent(token)}`
        : `${API_BASE}/invitations/${id}`;
      return await request<ApiDraft>(url);
    } catch (err) {
      if (!token) throw err;
    }
  }

  if (token) {
    const preview = await getDraftPreview(id, token);
    return preview as unknown as ApiDraft;
  }

  return request<ApiDraft>(`${API_BASE}/invitations/${id}`);
}

/** Fetch a draft invitation for unauthenticated preview (tokenized access). */
export async function getDraftPreview(id: string, token: string): Promise<ApiInvitation> {
  // This endpoint is unauthenticated — bypass the normal auth-injecting `request()` helper
  // and use a raw fetch to the public API.
  const baseUrl = API_BASE.replace(/\/v1$/, '');
  const res = await fetch(`${baseUrl}/v1/public/draft-preview/${encodeURIComponent(id)}?token=${encodeURIComponent(token)}`);
  
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = Array.isArray(body?.message)
      ? body.message.join(', ')
      : body?.message ?? res.statusText ?? 'Failed to fetch draft preview';
    throw new ApiError(message, res.status, body);
  }

  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as ApiInvitation;
}

/** List the user's drafts, most-recently-updated first (Drafts dashboard). */
export function listDrafts() {
  return request<PaginatedResponse<ApiDraft>>(
    `${API_BASE}/invitations?status=DRAFT&limit=100&sortBy=updatedAt&sortOrder=desc`,
  ).then((res) => res.items)
}

/** Duplicate a draft into a fresh DRAFT. */
export function duplicateDraft(id: string) {
  return request<ApiDraft>(`${API_BASE}/invitations/${id}/duplicate`, {
    method: 'POST',
  })
}

// ── Calendar integration ────────────────────────────────────────────────────
// Server-side projection of the user's invitations. Backed by CalendarEvent
// rows that the backend keeps in sync via invitation domain events.

export interface ApiCalendarEvent {
  id: string
  invitationId: string
  title: string
  description: string | null
  startDatetime: string
  endDatetime: string
  timezone: string
  venue: string | null
  status: string
  color: string | null
  createdAt: string
  updatedAt: string
}

export interface ApiUpcomingEvent {
  id: string
  invitationId: string
  title: string
  startDatetime: string
  venue: string | null
  status: string
  daysUntil: number
}

export interface CalendarEventsQuery {
  startDate?: string
  endDate?: string
  status?: string
  search?: string
}

export function getCalendarEvents(query: CalendarEventsQuery = {}) {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value) params.set(key, String(value))
  }
  const qs = params.toString()
  return request<ApiCalendarEvent[]>(
    `${API_BASE}/calendar/events${qs ? `?${qs}` : ''}`,
  )
}

export function getUpcomingEvents(limit = 10, windowDays = 30) {
  return request<ApiUpcomingEvent[]>(
    `${API_BASE}/calendar/upcoming?limit=${limit}&windowDays=${windowDays}`,
  )
}

export function getCalendarEvent(id: string) {
  return request<ApiCalendarEvent>(`${API_BASE}/calendar/events/${id}`)
}

export function deleteInvitation(id: string) {
  return request<void>(`${API_BASE}/invitations/${id}`, { method: 'DELETE' })
}

export function publishInvitation(id: string) {
  return request<ApiInvitation>(`${API_BASE}/invitations/${id}/publish`, { method: 'POST' })
    .then((inv) => {
      trackEvent('INVITATION_PUBLISHED', 'invitation', inv.occasionType, undefined, {
        occasion_type: inv.occasionType,
        template_id: inv.templateId,
        // Run total + template swaps, present only when published straight from the wizard.
        ...wizardSession.consume(Date.now(), inv.templateId),
      });
      return inv;
    })
}

export function validateShareLink(id: string) {
  return request<{ success: boolean }>(`${API_BASE}/invitations/${id}/share-link`, { method: 'POST' })
    .then((res) => {
      if (res.success) trackEvent('SHARE_LINK_GENERATED', 'invitation');
      return res;
    })
}

export function validateExportPdf(id: string) {
  return request<{ success: boolean }>(`${API_BASE}/invitations/${id}/export/pdf`)
}

export function validateExportGif(id: string) {
  return request<{ success: boolean }>(`${API_BASE}/invitations/${id}/export/gif`)
}

/**
 * Self-serve account deletion. Anonymizes PII, sets status=DELETED, disables
 * published invitations, and revokes sessions. The caller must sign out and
 * redirect to the landing page after this resolves.
 */
export function deleteAccount() {
  return request<{ success: true }>(`${API_BASE}/account/me`, { method: 'DELETE' })
}

// ── Media uploads (Cloudflare R2 through backend upload API) ────────────────
// Product uploads use multipart → backend → R2 so the browser never needs
// direct CORS access to the R2 S3 endpoint. The older presign helpers remain
// available for low-level diagnostics and future large-file flows.

export interface PresignedUpload {
  uploadUrl: string
  key: string
}

export interface ConfirmedUpload {
  processedUrl: string
  thumbnailUrl: string
  kind: 'image' | 'video'
  sizeBytes: number
  mimeType: string
}

/** Client-side mirror of the backend limits (server re-validates at confirm). */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024
export const MAX_VIDEO_BYTES = 300 * 1024 * 1024

export function getPresignedUploadUrl(
  invitationId: string,
  filename: string,
  contentType: string,
) {
  return request<PresignedUpload>(`${API_BASE}/media/presigned-url`, {
    method: 'POST',
    body: JSON.stringify({ invitationId, filename, contentType }),
  })
}

export function confirmMediaUpload(key: string) {
  return request<ConfirmedUpload>(`${API_BASE}/media/confirm`, {
    method: 'POST',
    body: JSON.stringify({ key }),
  })
}

/**
 * Full upload pipeline for a browser File: validates size locally, sends the
 * bytes to the backend, and lets the backend write/process/record the R2 asset.
 */
export async function uploadMediaFile(
  invitationId: string,
  file: File,
): Promise<ConfirmedUpload> {
  const isVideo = file.type.startsWith('video/')
  const maxBytes = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES
  if (file.size > maxBytes) {
    const mb = Math.round(maxBytes / 1024 / 1024)
    throw new Error(`File is too large — ${isVideo ? 'videos' : 'images'} must be under ${mb}MB.`)
  }

  const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'
  const token = isBypass ? 'dev-token' : await auth.currentUser?.getIdToken()
  const body = new FormData()
  body.set('invitationId', invitationId)
  body.set('file', file)

  const res = await fetch(`${API_BASE}/media/upload`, {
    method: 'POST',
    body,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })

  if (res.status === 401) {
    if (isBypass) {
      throw new Error('Unauthorized: ensure backend is also running with DEV_AUTH_BYPASS=true')
    }
    await logout().catch(() => undefined)
    window.location.href = '/login'
    throw new Error('Session expired')
  }

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}))
    const message = Array.isArray(errorBody?.message)
      ? errorBody.message.join(', ')
      : errorBody?.message ?? `Upload failed (HTTP ${res.status}). Please try again.`
    throw new Error(message)
  }

  const responseBody = await res.json()
  return (responseBody?.data !== undefined ? responseBody.data : responseBody) as ConfirmedUpload
}

export function getTemplates(occasionType?: string, limit = 120) {
  const params = new URLSearchParams()
  if (occasionType) params.set('occasionType', occasionType)
  params.set('limit', String(limit))

  return request<ApiTemplate[]>(`${API_BASE}/templates?${params.toString()}`)
}

export function getOccasions() {
  return request<{ occasions: ApiOccasion[] }>(`${API_BASE}/occasions`)
}

export function getPublicInvitation(slugOrId: string) {
  return request<ApiInvitation>(`${API_BASE}/public/${encodeURIComponent(slugOrId)}`)
}

export type SupportChatMessage = { role: 'user' | 'assistant'; content: string }

/**
 * Sends the full support-chat transcript and returns the assistant's reply.
 * The endpoint is stateless — the caller owns the transcript and replays it
 * on every turn; the last entry must be the new user message.
 */
export function sendSupportMessage(messages: SupportChatMessage[]) {
  return request<{ reply: string }>(`${API_BASE}/support/chat`, {
    method: 'POST',
    body: JSON.stringify({ messages }),
  })
}

/* ------------------------------------------------------------ Notifications */

/** All notification preference flags (mirrors the backend contract). */
export type NotificationPreferences = {
  // Legacy flags
  emailNotifications: boolean
  rsvpNotifications: boolean
  guestActivityUpdates: boolean
  reminderNotifications: boolean
  marketingEmails: boolean
  securityAlerts: boolean
  // Business-event category flags (added in notification domain expansion)
  invitationNotifications: boolean
  creditNotifications: boolean
  billingNotifications: boolean
  feedbackNotifications: boolean
  supportNotifications: boolean
  accountNotifications: boolean
}

export function getNotificationPreferences() {
  return request<NotificationPreferences>(`${API_BASE}/notifications/preferences`)
}

/** Partial update — `securityAlerts` is rejected by the backend, so never send it. */
export function updateNotificationPreferences(
  patch: Partial<Omit<NotificationPreferences, 'securityAlerts'>>,
) {
  return request<NotificationPreferences>(`${API_BASE}/notifications/preferences`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  })
}

export type ApiNotification = {
  id: string
  type: string
  category: string
  title: string
  message: string
  metadata: Record<string, unknown>
  isRead: boolean
  readAt: string | null
  createdAt: string
}

export type NotificationsPage = {
  items: ApiNotification[]
  page: number
  limit: number
  total: number
  totalPages: number
  unreadCount: number
}

export function getNotifications(params: { page?: number; limit?: number; unreadOnly?: boolean } = {}) {
  const qs = new URLSearchParams()
  if (params.page) qs.set('page', String(params.page))
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.unreadOnly) qs.set('unreadOnly', 'true')
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  return request<NotificationsPage>(`${API_BASE}/notifications${suffix}`)
}

export function getUnreadNotificationCount() {
  return request<{ unreadCount: number }>(`${API_BASE}/notifications/unread-count`)
}

export function markNotificationRead(id: string) {
  return request<ApiNotification>(`${API_BASE}/notifications/${id}/read`, { method: 'PATCH' })
}

export function markAllNotificationsRead() {
  return request<{ updated: number }>(`${API_BASE}/notifications/read-all`, { method: 'PATCH' })
}

export function deleteNotification(id: string) {
  return request<{ id: string; deleted: true }>(`${API_BASE}/notifications/${id}`, {
    method: 'DELETE',
  })
}

/* ---------------------------------------------------------------- Feedback */

export type FeedbackType =
  | 'BUG'
  | 'FEATURE_REQUEST'
  | 'GENERAL'
  | 'PAYMENT_ISSUE'
  | 'PERFORMANCE'
  | 'UI_UX'
  | 'OTHER'

export type FeedbackStatus =
  | 'OPEN'
  | 'IN_REVIEW'
  | 'RESOLVED'
  | 'REJECTED'
  | 'CLOSED'

export type FeedbackPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'

export type FeedbackAttachment = {
  id: string
  fileUrl: string
  fileType: string
  fileSize: number
  createdAt: string
}

export type FeedbackActivity = {
  id: string
  action:
    | 'CREATED'
    | 'STATUS_CHANGED'
    | 'PRIORITY_CHANGED'
    | 'RESPONDED'
    | 'DELETED'
    | 'ATTACHMENT_ADDED'
  performedById: string | null
  metadata: Record<string, unknown>
  createdAt: string
}

export type ApiFeedback = {
  id: string
  userId: string
  user?: { id: string; email: string; name: string; avatar: string | null } | null
  type: FeedbackType
  title: string
  description: string
  rating: number | null
  status: FeedbackStatus
  priority: FeedbackPriority
  adminResponse: string | null
  metadata: Record<string, unknown>
  attachments: FeedbackAttachment[]
  activity?: FeedbackActivity[]
  resolvedAt: string | null
  resolvedBy?: { id: string; email: string; name: string } | null
  createdAt: string
  updatedAt: string
}

export type FeedbackListPage = {
  items: ApiFeedback[]
  meta: { page: number; limit: number; total: number; totalPages: number }
}

export type CreateFeedbackPayload = {
  type: FeedbackType
  title: string
  description: string
  rating?: number
  attachments?: { fileUrl: string; fileType: string; fileSize: number }[]
  metadata?: { platform?: string; browser?: string; appVersion?: string }
}

export type ListFeedbackParams = {
  page?: number
  limit?: number
  status?: FeedbackStatus
  type?: FeedbackType
  search?: string
}

function feedbackQuery(params: ListFeedbackParams = {}) {
  const qs = new URLSearchParams()
  if (params.page) qs.set('page', String(params.page))
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.status) qs.set('status', params.status)
  if (params.type) qs.set('type', params.type)
  if (params.search) qs.set('search', params.search)
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export function createFeedback(payload: CreateFeedbackPayload) {
  return request<ApiFeedback>(`${API_BASE}/feedback`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function listMyFeedback(params: ListFeedbackParams = {}) {
  return request<FeedbackListPage>(`${API_BASE}/feedback/my${feedbackQuery(params)}`)
}

export function getMyFeedback(id: string) {
  return request<ApiFeedback>(`${API_BASE}/feedback/${id}`)
}

export function withdrawFeedback(id: string) {
  return request<void>(`${API_BASE}/feedback/${id}`, { method: 'DELETE' })
}

// ── Help & Support ──────────────────────────────────────────────────────────

export type SupportTicketStatus =
  | 'OPEN'
  | 'IN_PROGRESS'
  | 'WAITING_FOR_USER'
  | 'RESOLVED'
  | 'CLOSED'

export type SupportTicketPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'

export type SupportTicketCategory =
  | 'ACCOUNT'
  | 'BILLING'
  | 'TEMPLATE'
  | 'INVITATION'
  | 'PUBLISHING'
  | 'RSVP'
  | 'MEDIA'
  | 'BUG'
  | 'FEATURE_REQUEST'
  | 'OTHER'

export type SupportSenderType = 'USER' | 'ADMIN' | 'SYSTEM'

export interface SupportAttachment {
  id: string
  fileUrl: string
  fileType: string
  fileSize: number
  createdAt: string
}

export interface SupportTicketMessage {
  id: string
  ticketId: string
  senderType: SupportSenderType
  senderId: string | null
  sender: { id: string; email: string; name: string; avatar: string | null; role?: string } | null
  message: string
  internalNote: boolean
  attachments: SupportAttachment[]
  createdAt: string
}

export interface ApiSupportTicket {
  id: string
  userId: string
  user: { id: string; email: string; name: string; avatar: string | null } | null
  subject: string
  category: SupportTicketCategory
  priority: SupportTicketPriority
  status: SupportTicketStatus
  description: string
  assignedAdminId: string | null
  assignedAdmin: { id: string; email: string; name: string; avatar: string | null } | null
  metadata: Record<string, unknown>
  firstResponseAt: string | null
  resolvedAt: string | null
  closedAt: string | null
  lastActivityAt: string
  messageCount: number
  messages: SupportTicketMessage[]
  createdAt: string
  updatedAt: string
}

export type SupportTicketPage = {
  items: ApiSupportTicket[]
  meta: { page: number; limit: number; total: number; totalPages: number }
}

export interface CreateSupportTicketPayload {
  subject: string
  category: SupportTicketCategory
  description: string
  priority?: SupportTicketPriority
  attachments?: { fileUrl: string; fileType: string; fileSize: number }[]
  metadata?: { platform?: string; browser?: string; appVersion?: string; invitationId?: string }
}

export interface ListSupportTicketsParams {
  page?: number
  limit?: number
  status?: SupportTicketStatus
  category?: SupportTicketCategory
  priority?: SupportTicketPriority
  search?: string
}

function ticketQuery(params: ListSupportTicketsParams = {}) {
  const qs = new URLSearchParams()
  if (params.page) qs.set('page', String(params.page))
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.status) qs.set('status', params.status)
  if (params.category) qs.set('category', params.category)
  if (params.priority) qs.set('priority', params.priority)
  if (params.search) qs.set('search', params.search)
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export function createSupportTicket(payload: CreateSupportTicketPayload) {
  return request<ApiSupportTicket>(`${API_BASE}/support/tickets`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function listMyTickets(params: ListSupportTicketsParams = {}) {
  return request<SupportTicketPage>(`${API_BASE}/support/tickets${ticketQuery(params)}`)
}

export function getMyTicket(id: string) {
  return request<ApiSupportTicket>(`${API_BASE}/support/tickets/${id}`)
}

export function replyToTicket(
  id: string,
  payload: { message: string; attachments?: { fileUrl: string; fileType: string; fileSize: number }[] },
) {
  return request<ApiSupportTicket>(`${API_BASE}/support/tickets/${id}/messages`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function withdrawTicket(id: string) {
  return request<void>(`${API_BASE}/support/tickets/${id}`, { method: 'DELETE' })
}

// ── FAQ / help center (public, no auth required) ───────────────────────────

export type FaqCategoryKey =
  | 'GETTING_STARTED'
  | 'TEMPLATES'
  | 'INVITATIONS'
  | 'RSVP'
  | 'BILLING'
  | 'ACCOUNT'
  | 'TROUBLESHOOTING'
  | 'OTHER'

export interface ApiFaqArticle {
  id: string
  title: string
  slug: string
  category: FaqCategoryKey
  content: string
  excerpt: string | null
  tags: string[]
  isPublished: boolean
  sortOrder: number
  viewCount: number
  helpfulCount: number
  notHelpfulCount: number
  publishedAt: string | null
  author: { id: string; email: string; name: string } | null
  createdAt: string
  updatedAt: string
}

export type FaqListPage = {
  items: ApiFaqArticle[]
  meta: { page: number; limit: number; total: number; totalPages: number }
}

export interface ListFaqsParams {
  page?: number
  limit?: number
  category?: FaqCategoryKey
  search?: string
}

function faqQuery(params: ListFaqsParams = {}) {
  const qs = new URLSearchParams()
  if (params.page) qs.set('page', String(params.page))
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.category) qs.set('category', params.category)
  if (params.search) qs.set('search', params.search)
  const s = qs.toString()
  return s ? `?${s}` : ''
}

/** Public — uses bare fetch (no auth header) so unauthenticated visitors work. */
async function publicGet<T>(url: string): Promise<T> {
  const res = await fetch(url)
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.message ?? `HTTP ${res.status}`)
  }
  if (res.status === 204) return undefined as T
  const body = await res.json()
  return (body?.data !== undefined ? body.data : body) as T
}

export function listFaqs(params: ListFaqsParams = {}) {
  return publicGet<FaqListPage>(`${API_BASE}/support/faqs${faqQuery(params)}`)
}

export function getFaqCategories() {
  return publicGet<Record<string, number>>(`${API_BASE}/support/faqs/categories`)
}

export function getFaqBySlug(slug: string) {
  return publicGet<ApiFaqArticle>(`${API_BASE}/support/faqs/${slug}`)
}

export async function voteOnFaq(id: string, helpful: boolean) {
  const res = await fetch(`${API_BASE}/support/faqs/${id}/vote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ helpful }),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const body = await res.json()
  return (body?.data ?? body) as ApiFaqArticle
}

// ── Public guest submissions (no auth) ──────────────────────────────────────
// Used by published invitation pages where the guest is not logged in. Avoids
// `request()` so a missing token never triggers the auth redirect path.
export async function publicPost<T>(url: string, payload: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.message ?? `HTTP ${res.status}`)
  }
  if (res.status === 204) return undefined as T
  const body = await res.json()
  return (body?.data !== undefined ? body.data : body) as T
}

// ── Public templates (no-auth showcase) ────────────────────────────────────

export function publicGetTemplates(occasionType?: string) {
  const params = new URLSearchParams()
  if (occasionType) params.set('occasionType', occasionType)
  params.set('limit', '120')
  return publicGet<ApiTemplate[]>(`${API_BASE}/templates?${params.toString()}`)
}

// ── RSVP — in-app guest submission ──────────────────────────────────────────

export type RsvpStatus = 'ACCEPTED' | 'DECLINED' | 'MAYBE'

/** Public — submit an RSVP for a published invitation. */
export async function trackPublicInvitationView(invitationId: string, anonViewerId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/notifications/track/${invitationId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'VIEWED', anonViewerId }),
  });
  if (!response.ok) {
    throw new ApiError('Failed to track view', response.status);
  }
}

export function submitRsvp(
  invitationId: string,
  payload: {
    guestName: string
    guestEmail?: string
    status: RsvpStatus
    headcount?: number
    message?: string
  },
) {
  return publicPost<unknown>(`${API_BASE}/rsvp/${invitationId}`, payload)
    .then((res) => {
      // Same name/label/status as main; template params added when submitted from a guest template.
      trackEvent('RSVP_SUBMITTED', 'engagement', payload.status, undefined, { ...templateContextParams(), status: payload.status });
      return res;
    })
}

// ── Unified Guest Messages — Guestbook ───────────────────────────────────────

import { GuestMessage } from '@/src/types'

export interface PublicWish {
  id: string
  guestName: string
  message: string
  createdAt: string
}

/** Public — approved messages (wishes or blessings) for the live wall. */
export function getGuestMessages(invitationId: string, offset?: number, limit?: number) {
  const params = new URLSearchParams()
  if (offset !== undefined) params.append('offset', offset.toString())
  if (limit !== undefined) params.append('limit', limit.toString())
  const query = params.toString() ? `?${params.toString()}` : ''
  return publicGet<GuestMessage[]>(`${API_BASE}/guestbook/${invitationId}/messages${query}`)
}

/** Public — post a guest message. */
export function postGuestMessage(
  invitationId: string,
  payload: { guestName: string; message: string; relationship?: string; photoUrl?: string },
) {
  return publicPost<GuestMessage>(`${API_BASE}/guestbook/${invitationId}/messages`, payload).then((res) => {
    // Never the name/message — only which wall and the template context.
    trackEvent('GUESTBOOK_MESSAGE_POSTED', 'guest', 'messages', undefined, { ...templateContextParams(), section: 'messages' })
    return res
  })
}

/** Public — react to a message. */
export function reactToGuestMessage(messageId: string, type: string) {
  return publicPost<GuestMessage>(`${API_BASE}/guestbook/messages/${messageId}/react`, { type })
}

/** Public — upload guest photo. Capped at 5MB. Returns URLs. */
export async function uploadGuestPhoto(invitationId: string, file: File) {
  const formData = new FormData()
  formData.append('file', file)
  const res = await fetch(`${API_BASE}/guestbook/${invitationId}/upload`, {
    method: 'POST',
    body: formData,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.message ?? `HTTP ${res.status}`)
  }
  const body = await res.json()
  return (body?.data !== undefined ? body.data : body) as { processedUrl: string; thumbnailUrl: string }
}

/** Authenticated owner — read all guest messages (including pending/hidden) for moderation. */
export function getGuestMessagesForOwner(invitationId: string, offset?: number, limit?: number) {
  const params = new URLSearchParams()
  if (offset !== undefined) params.append('offset', offset.toString())
  if (limit !== undefined) params.append('limit', limit.toString())
  const query = params.toString() ? `?${params.toString()}` : ''
  return request<GuestMessage[]>(`${API_BASE}/guestbook/${invitationId}/messages/owner${query}`)
}

/** Authenticated owner — approve, reject, or show/hide a message. */
export function updateGuestMessageStatus(
  messageId: string,
  payload: { isApproved?: boolean; showPublicly?: boolean },
) {
  return request<GuestMessage>(`${API_BASE}/guestbook/messages/${messageId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

/** Authenticated owner — permanently delete a message. */
export function deleteGuestMessage(messageId: string) {
  return request<{ deleted: boolean }>(`${API_BASE}/guestbook/messages/${messageId}`, {
    method: 'DELETE',
  })
}

/** Public — approved wishes for the live Wishes Wall (Deprecated). */
export function getBirthdayWishes(invitationId: string) {
  return publicGet<PublicWish[]>(`${API_BASE}/guestbook/${invitationId}/wishes`)
}

/** Public — post a birthday wish; returns the created (approved) wish (Deprecated). */
export function postBirthdayWish(
  invitationId: string,
  payload: { guestName: string; message: string },
) {
  return publicPost<PublicWish>(`${API_BASE}/guestbook/${invitationId}/wishes`, payload).then((res) => {
    // Never the name/message — only which wall and the template context.
    trackEvent('GUESTBOOK_MESSAGE_POSTED', 'guest', 'wishes', undefined, { ...templateContextParams(), section: 'wishes' })
    return res
  })
}

/** Public — leave a private Memory Capsule message (acknowledgement only). */
export function postCapsuleMessage(
  invitationId: string,
  payload: { senderName: string; message: string },
) {
  return publicPost<{ ok: true }>(`${API_BASE}/guestbook/${invitationId}/capsule`, payload).then((res) => {
    // Never the name/message — only which wall and the template context.
    trackEvent('GUESTBOOK_MESSAGE_POSTED', 'guest', 'capsule', undefined, { ...templateContextParams(), section: 'capsule' })
    return res
  })
}

/** Public — get capsule message count for the submission form badge (no content). */
export function getCapsuleMessageCount(invitationId: string) {
  return publicGet<{ count: number }>(`${API_BASE}/guestbook/${invitationId}/capsule/count`)
}

/** Authenticated owner — read all capsule messages with approval status. */
export function getCapsuleMessageOwner(invitationId: string) {
  return request<{ id: string; senderName: string; message: string; createdAt: string; approved: boolean }[]>(
    `${API_BASE}/guestbook/${invitationId}/capsule/owner`,
  )
}

// ── Memory Capsule Gift ─────────────────────────────────────────────────────

export interface GiftMessage {
  id: string
  senderName: string
  message: string
  createdAt: string
  displayOrder: number | null
}

export interface MemoryCapsuleGift {
  id: string
  invitationId: string
  title: string
  subtitle: string
  recipientName: string
  coverImage: string | null
  generatedAt: string
  totalMessages: number
  totalSenders: number
  giftSlug: string
  isPublic: boolean
  messages: GiftMessage[]
}

export interface CapsuleGiftStats {
  totalMessages: number
  totalSenders: number
  hasGift: boolean
  giftSlug: string | null
  shareToken: string | null
  generatedAt: string | null
}

export interface GenerateGiftPayload {
  recipientName: string
  title?: string
  subtitle?: string
  coverImage?: string
  isPublic?: boolean
}

/** Owner — generate or regenerate the gift for an invitation. */
export function generateCapsuleGift(invitationId: string, payload: GenerateGiftPayload) {
  return request<MemoryCapsuleGift>(`${API_BASE}/capsule-gift/${invitationId}/generate`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

/** Owner — get the generated gift (with messages). */
export function getMyGift(invitationId: string) {
  return request<MemoryCapsuleGift>(`${API_BASE}/capsule-gift/${invitationId}/mine`)
}

/** Owner — get capsule stats before generating the gift. */
export function getCapsuleGiftStats(invitationId: string) {
  return request<CapsuleGiftStats>(`${API_BASE}/capsule-gift/${invitationId}/stats`)
}

/** Owner — approve or hide an individual capsule message. */
export function setCapsuleMessageApproval(messageId: string, approved: boolean) {
  return request<{ ok: true }>(`${API_BASE}/capsule-gift/messages/${messageId}/approval`, {
    method: 'PATCH',
    body: JSON.stringify({ approved }),
  })
}

/** Public — view gift by public slug. */
export function getPublicGiftBySlug(giftSlug: string) {
  return publicGet<MemoryCapsuleGift>(`${API_BASE}/capsule-gift/view/${giftSlug}`)
}

/** Public — view gift by share token. */
export function getPublicGiftByToken(shareToken: string) {
  return publicGet<MemoryCapsuleGift>(`${API_BASE}/capsule-gift/open/${shareToken}`)
}

// ── MCP OAuth (InviteCloud AI desktop extension) ────────────────────────────

/** Approve a pending MCP OAuth authorization from the logged-in consent page. */
export function approveMcpAuthorize(params: {
  redirect_uri: string
  code_challenge: string
  code_challenge_method: string
  state?: string
}) {
  return request<{ redirectTo: string }>(`${API_BASE}/ai/mcp/oauth/authorize/approve`, {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

/** Returns the PDF export URL for the gift (download link). */
export function getCapsuleGiftPdfUrl(giftSlug: string) {
  return `${API_BASE}/capsule-gift/view/${giftSlug}/export/pdf`
}

export function getCapsuleGiftPdfUrlByToken(shareToken: string) {
  return `${API_BASE}/capsule-gift/open/${shareToken}/export/pdf`
}
